<template>
  <div class="bom-form-inline">

    <!-- Section: Product & Batch Info -->
    <div class="bom-section-title">Product &amp; Batch Info</div>

    <div class="form-row">
      <!-- Output Product -->
      <div class="form-group col-12 col-md-5">
        <label class="bom-label">Output Product <span class="text-danger">*</span></label>
        <div v-if="editBom" class="form-control bg-light">{{ form.product_name }}</div>
        <div v-else class="position-relative">
          <input
            type="text"
            class="form-control"
            v-model="productSearch"
            @input="searchProducts"
            :placeholder="form.product_name || 'Search the product this recipe makes…'"
            autocomplete="off"
          />
          <div v-if="productSuggestions.length" class="bom-typeahead">
            <button
              v-for="p in productSuggestions"
              :key="p.id"
              type="button"
              class="bom-typeahead-item"
              @mousedown.prevent="selectProduct(p)"
            >
              <strong>{{ p.product }}</strong>
              <small class="text-muted d-block">
                {{ p.remaining_units }} {{ p.unit || '' }} in stock
                <span v-if="p.has_approved_recipe"> · has a recipe</span>
                <span v-else-if="p.sold_as_recipe"> · sold at the counter as a recipe</span>
              </small>
            </button>
          </div>
        </div>
        <small v-if="editBom" class="text-secondary mt-1 d-block">
          Editing v{{ editBom.version }}. Saving creates <strong>v{{ (editBom.latest_version || editBom.version) + 1 }}</strong> as a draft for approval; v{{ editBom.version }} stays as it is{{ editBom.status === 'approved' ? ' and keeps running production until the new version is approved' : '' }}.
        </small>
        <small v-else-if="form.product_name" class="text-dark mt-1 d-block">
          <i class="bi bi-check-circle mr-1"></i>{{ form.product_name }}
        </small>
        <small v-if="selectedOutput && selectedOutput.has_approved_recipe" class="text-secondary d-block mt-1">
          This product already has an approved recipe — saving creates a new version for approval.
        </small>
        <div v-if="selectedOutput && selectedOutput.sold_as_recipe" class="alert alert-secondary py-2 px-3 mt-2 mb-0" style="font-size:12px;">
          <strong>{{ selectedOutput.product }}</strong> is sold at the counter as a recipe today (its materials are deducted at each sale).
          Once this recipe is approved it will be made in batches instead: you sell the finished product from stock, and materials are used when you produce. Nothing changes until the recipe is approved.
        </div>
      </div>

      <!-- BOM Name -->
      <div class="form-group col-12 col-md-4">
        <label class="bom-label">BOM Name <span class="text-danger">*</span></label>
        <input type="text" class="form-control" v-model="form.name" placeholder="e.g. Chapati v1" />
      </div>

      <!-- Type -->
      <div class="form-group col-6 col-md-2">
        <label class="bom-label">Type</label>
        <select class="form-control" v-model="form.bom_type">
          <option value="fixed">Fixed</option>
          <option value="ratio">Ratio %</option>
        </select>
      </div>

      <!-- Yield % -->
      <div class="form-group col-6 col-md-1">
        <label class="bom-label">Yield %</label>
        <input
          type="number"
          class="form-control"
          v-model.number="form.expected_yield_pct"
          min="1"
          max="100"
          step="0.1"
        />
      </div>
    </div>

    <div class="form-row">
      <!-- Batch Size + Unit -->
      <div class="form-group col-12 col-md-3">
        <label class="bom-label">Batch Size <span class="text-danger">*</span></label>
        <div class="input-group">
          <input type="number" class="form-control" v-model.number="form.batch_size" min="0.0001" step="any" placeholder="0" />

          <div class="input-group-append">
            <input type="text" class="form-control" style="max-width:64px;" v-model="form.batch_unit" placeholder="pcs" />
          </div>
        </div>
      </div>

      <!-- Conversion cost -->
      <div class="form-group col-12 col-md-3">
        <label class="bom-label">Conversion cost / {{ form.batch_unit || 'unit' }}</label>
        <input type="number" class="form-control" v-model.number="form.conversion_cost_per_unit" min="0" step="any" placeholder="0" />
      </div>

      <!-- Notes -->
      <div class="form-group col-12 col-md-6">
        <label class="bom-label">Notes</label>
        <input type="text" class="form-control" v-model="form.notes" placeholder="Production notes..." />
      </div>
    </div>

    <!-- Section: Stages -->
    <div class="bom-section-title mt-3">Production Stages</div>

    <div v-for="(stage, si) in form.stages" :key="stage.temp_uid" class="bom-stage-block">

      <!-- Stage header: full-width Bootstrap row -->
      <div class="bom-stage-top">
        <div class="form-row align-items-end w-100">
          <div class="form-group col-md-6 mb-2">
            <label class="bom-label">Stage {{ stage.stage_order }} name</label>
            <input type="text" class="form-control" v-model="stage.name" placeholder="e.g. Mixing" />
          </div>
          <div class="form-group col-md-3 mb-2">
            <label class="bom-label">Wastage</label>
            <select class="custom-select" v-model="stage.wastage_tracking">
              <option value="none">No wastage</option>
              <option value="optional">Wastage optional</option>
              <option value="required">Wastage required</option>
            </select>
          </div>
          <div class="form-group col-md-2 mb-2">
            <label class="bom-label">Expected loss</label>
            <div class="input-group">
              <input type="number" class="form-control" v-model.number="stage.expected_wastage_pct" min="0" max="100" step="0.1" placeholder="0" />
              <div class="input-group-append"><span class="input-group-text">%</span></div>
            </div>
          </div>
          <div class="form-group col-md-1 mb-2 text-right">
            <button class="btn btn-outline-danger" @click="removeStage(si)" :disabled="form.stages.length <= 1" title="Remove stage">
              <i class="bi bi-trash"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- Materials -->
      <div class="bom-materials">

        <!-- Column headings (desktop) -->
        <div v-if="stage.materials.length" class="form-row d-none d-md-flex text-muted bom-label mb-1">
          <div class="col-md-4">Ingredient</div>
          <div class="col-md-3">Quantity &amp; unit</div>
          <div class="col-md-2">Loss allowance</div>
          <div class="col-md-1 text-right">Value</div>
          <div class="col-md-2 text-right">Required / Substitutable</div>
        </div>

        <!-- One Bootstrap row per ingredient -->
        <div v-for="(mat, mi) in stage.materials" :key="mat.temp_uid" class="form-row align-items-center py-2 border-top">
          <div class="col-md-4 mb-2 mb-md-0">
            <span class="bom-mat-name">{{ mat.product ? mat.product.product : 'Deleted item' }}</span>
            <small v-if="mat.product && mat.product.sku" class="text-muted d-block">{{ mat.product.sku }}</small>
          </div>
          <div class="col-6 col-md-3 mb-2 mb-md-0">
            <div class="input-group">
              <input type="number" class="form-control" v-model.number="mat.quantity" min="0.0001" step="any" placeholder="Qty" />
              <input type="text" class="form-control" style="max-width:90px;" v-model="mat.unit" placeholder="unit" />
            </div>
          </div>
          <div class="col-6 col-md-2 mb-2 mb-md-0">
            <div class="input-group">
              <input type="number" class="form-control" v-model.number="mat.wastage_allowance_pct" min="0" max="100" step="0.1" placeholder="0" />
              <div class="input-group-append"><span class="input-group-text">%</span></div>
            </div>
          </div>
          <div class="col-6 col-md-1 text-md-right">
            <template v-if="lineValue(mat) !== null">{{ money(lineValue(mat)) }}</template>
            <small v-else class="text-danger" :title="valueHint(mat)">not counted</small>
          </div>
          <div class="col-6 col-md-2 d-flex justify-content-end align-items-center" style="gap:12px;">
            <div class="custom-control custom-switch" title="Required">
              <input type="checkbox" class="custom-control-input" :id="'req_'+si+'_'+mi" v-model="mat.is_required" />
              <label class="custom-control-label" :for="'req_'+si+'_'+mi"></label>
            </div>
            <div class="custom-control custom-switch" title="Substitutable">
              <input type="checkbox" class="custom-control-input" :id="'sub_'+si+'_'+mi" v-model="mat.is_substitutable" />
              <label class="custom-control-label" :for="'sub_'+si+'_'+mi"></label>
            </div>
            <button class="btn btn-link text-danger p-0" @click="removeMaterial(si, mi)" title="Remove"><i class="bi bi-x-circle-fill"></i></button>
          </div>
        </div>

        <div v-if="stage.materials.length" class="d-flex justify-content-end flex-wrap border-top pt-2 mb-2" style="gap:24px;">
          <div class="text-right"><small class="text-muted d-block">Total quantity</small><strong class="text-dark">{{ qtyText(stage.materials) }}</strong></div>
          <div class="text-right"><small class="text-muted d-block">Value</small><strong class="text-dark">{{ money(stageValue(stage)) }}</strong></div>
        </div>

        <p v-else-if="!stage.materials.length" class="text-muted mb-2">No materials yet. Search below to add, or leave this stage empty (e.g. proofing, curing, drying).</p>

        <!-- Add ingredient typeahead -->
        <div class="position-relative mt-2">
          <div class="input-group">
            <input
              type="text"
              class="form-control"
              v-model="stage.materialSearch"
              @input="onMaterialSearch(si)"
              @focus="stage.showDropdown = true"
              placeholder="Add material..."
              autocomplete="off"
            />
            <div class="input-group-append">
              <span class="input-group-text bg-white"><i class="bi bi-search text-muted"></i></span>
            </div>
          </div>
          <div
            v-if="stage.showDropdown && ((stage.materialSuggestions && stage.materialSuggestions.length) || canCreateMaterial(stage))"
            class="bom-typeahead"
          >
            <button
              v-for="item in stage.materialSuggestions"
              :key="item.id"
              type="button"
              class="bom-typeahead-item"
              @mousedown.prevent="addMaterial(si, item)"
            >
              <span>{{ item.product }}</span>
              <small :class="item.remaining_units <= 0 ? 'text-danger' : 'text-muted'" class="ml-auto">
                {{ item.remaining_units }} {{ item.measuring_unit }}
              </small>
            </button>
            <button v-if="canCreateMaterial(stage)" type="button" class="bom-typeahead-item text-primary" @mousedown.prevent="startNewMaterial(si)">
              <span><i class="bi bi-plus-circle mr-1"></i>Create “{{ stage.materialSearch.trim() }}” as a new item</span>
            </button>
          </div>
        </div>

        <!-- New ingredient without leaving the recipe (starts at zero stock) -->
        <div v-if="newMaterial.stage === si" class="border rounded p-3 mt-2" style="max-width:640px;">
          <div class="form-row">
            <div class="form-group col-md-6">
              <label class="bom-label">Item name</label>
              <input v-model="newMaterial.product" type="text" class="form-control" maxlength="255">
            </div>
            <div class="form-group col-md-6">
              <label class="bom-label">Category</label>
              <select v-model="newMaterial.productcat_id" class="custom-select">
                <option :value="null" disabled>Choose…</option>
                <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name }}</option>
              </select>
            </div>
            <div class="form-group col-md-4">
              <label class="bom-label">Unit</label>
              <input v-model="newMaterial.measuring_unit" type="text" class="form-control" maxlength="50" placeholder="kg">
            </div>
            <div class="form-group col-md-4">
              <label class="bom-label">Buying price / {{ newMaterial.measuring_unit || 'unit' }}</label>
              <input v-model.number="newMaterial.wholesale_ppu" type="number" min="0" step="any" class="form-control" placeholder="0">
            </div>
            <div class="form-group col-md-4 d-flex align-items-end">
              <div class="custom-control custom-checkbox mb-2">
                <input :id="'nm_sell_'+si" v-model="newMaterial.can_be_sold" type="checkbox" class="custom-control-input">
                <label class="custom-control-label" :for="'nm_sell_'+si">Also sold at the counter</label>
              </div>
            </div>
          </div>
          <small class="text-muted d-block mb-2">It's added to inventory with zero stock; restock it as usual. The buying price is what this recipe is costed at.</small>
          <p v-if="newMaterial.error" class="text-danger small mb-2">{{ newMaterial.error }}</p>
          <div class="d-flex" style="gap:8px;">
            <button type="button" class="btn btn-primary" :disabled="newMaterial.saving || !newMaterial.product || !newMaterial.productcat_id || !newMaterial.measuring_unit" @click="saveNewMaterial(si)">
              <span v-if="newMaterial.saving" class="spinner-border spinner-border-sm mr-1"></span>Create and add
            </button>
            <button type="button" class="btn btn-outline-secondary" @click="newMaterial = emptyNewMaterial()">Cancel</button>
          </div>
        </div>

      </div>
    </div>

    <!-- Recipe value: what the entered quantities cost at current buying prices -->
    <div v-if="materialCount" class="d-flex flex-wrap justify-content-end align-items-baseline mt-3" style="gap:24px;">
      <div v-if="qtyTotalText" class="text-right">
        <small class="text-muted d-block">Total quantity</small>
        <strong class="text-dark" style="font-size:1.1rem;">{{ qtyTotalText }}</strong>
        <small v-if="form.batch_size > 0" class="text-muted d-block">batch {{ form.batch_size }} {{ form.batch_unit }}</small>
      </div>
      <div class="text-right">
        <small class="text-muted d-block">Materials value</small>
        <strong class="text-dark" style="font-size:1.1rem;">{{ money(recipeValue.total) }}</strong>
      </div>
      <div v-if="form.batch_size > 0" class="text-right">
        <small class="text-muted d-block">Per {{ form.batch_unit || 'unit' }} ({{ form.batch_size }} {{ form.batch_unit }} batch)</small>
        <strong class="text-dark">{{ money(recipeValue.total / form.batch_size) }}</strong>
      </div>
      <div v-if="recipeValue.missing" class="text-danger small w-100 text-right">
        {{ recipeValue.missing }} item(s) not counted:
        <div v-for="r in missingReasons" :key="r">{{ r }}</div>
      </div>
    </div>

    <!-- New recipe only: protect it with your PIN (enforced server-side). -->
    <div v-if="!editBom" class="mt-3">
      <div class="custom-control custom-switch mb-2">
        <input id="bomProtect" v-model="protect.on" type="checkbox" class="custom-control-input">
        <label class="custom-control-label" for="bomProtect"><i class="bi bi-lock mr-1"></i>Protect this recipe with a PIN</label>
      </div>
      <div v-if="protect.on" class="form-row">
        <div class="form-group col-md-6"><input v-model="protect.pin" type="password" inputmode="numeric" maxlength="8" autocomplete="new-password" class="form-control" placeholder="PIN (4–8 digits)"></div>
        <div class="form-group col-md-6"><input v-model="protect.pin_confirmation" type="password" inputmode="numeric" maxlength="8" autocomplete="new-password" class="form-control" placeholder="Confirm PIN"></div>
        <small class="col-12 text-muted">Everyone, including admins, will see only the title until they enter this PIN. Only you can change it; if lost, only Dukakit support can reset it.</small>
      </div>
    </div>

    <!-- Footer: Add Stage + Actions -->
    <div class="d-flex align-items-center justify-content-between mt-3 pt-3 border-top">
      <button class="btn btn-sm btn-outline-primary" @click="addStage">
        <i class="bi bi-plus-circle mr-1"></i> Add Stage
      </button>
      <div class="d-flex" style="gap:8px;">
        <button class="btn btn-sm btn-outline-secondary" @click="$emit('cancel')">Cancel</button>
        <button class="btn btn-sm btn-primary px-3" @click="save" :disabled="saving">
          <span v-if="saving" class="spinner-border spinner-border-sm mr-1"></span>
          {{ saving ? 'Saving...' : 'Save BOM' }}
        </button>
      </div>
    </div>

  </div>
</template>

<script>
let _uid = 0;
const uid = () => ++_uid;

export default {
  name: 'BomFormInline',
  props: {
    editBom: { type: Object, default: null },
    // true: save over this version (drafts only); false: save as a new version.
    inPlace: { type: Boolean, default: false },
  },
  data() {
    return {
      selectedOutput: null,
      protect: { on: false, pin: '', pin_confirmation: '' },
      saving: false,
      productSearch: '',
      productSuggestions: [],
      allInventoryItems: [],
      // Line values from the server (real unit conversions), by material temp_uid.
      serverValues: {},
      categories: [],
      newMaterial: this.emptyNewMaterial(),
      form: this.emptyForm(),
    };
  },
  mounted() {
    this.loadInventoryItems();
    if (this.editBom) this.populateFromBom(this.editBom);
  },
  computed: {
    /** Sum of the quantities as entered, per unit (e.g. "500 kg" or "500 kg + 12 pcs"). */
    qtyTotalText() {
      return this.qtyText(this.form.stages.flatMap(st => st.materials));
    },
    missingReasons() {
      return this.form.stages.flatMap(st => st.materials).filter(m => this.lineValue(m) === null).map(m => this.valueHint(m));
    },
    materialCount() {
      return this.form.stages.reduce((n, st) => n + st.materials.length, 0);
    },
    recipeValue() {
      let total = 0, missing = 0, qty = 0;
      this.form.stages.forEach(st => st.materials.forEach(m => {
        const v = this.lineValue(m);
        if (v === null) missing++; else total += v;
        const f = this.unitFactor(m.unit, this.form.batch_unit);
        if (f !== null) qty += Number(m.quantity || 0) * f;
      }));
      return { total, missing, qty };
    },
  },
  watch: {
    'form.stages': {
      deep: true,
      handler() { clearTimeout(this._valT); this._valT = setTimeout(() => this.fetchValues(), 400); },
    },
    editBom(bom) { if (bom) this.populateFromBom(bom); else this.reset(); },
  },
  methods: {
    // ── Values: quantity x current buying price (per the item's own unit) ──
    costOf(mat) {
      const live = this.allInventoryItems.find(i => i.id === (mat.product_id || (mat.product && mat.product.id)));
      const cost = (live || mat.product || {}).wholesale_ppu;
      return cost === null || cost === undefined || cost === '' ? null : Number(cost);
    },
    unitFactor(from, to) {
      const f = (from || '').trim().toLowerCase(), t = (to || '').trim().toLowerCase();
      if (f === t) return 1;
      const tables = [{ kg: 1000, g: 1, mg: 0.001 }, { l: 1000, ltr: 1000, litre: 1000, liter: 1000, ml: 1 }];
      const tbl = tables.find(x => x[f] !== undefined && x[t] !== undefined);
      return tbl ? tbl[f] / tbl[t] : null;
    },
    /** Quantities as entered, summed per unit ("1000 kg + 1 packet"). */
    qtyText(materials) {
      const byUnit = {};
      materials.forEach(m => {
        const u = (m.unit || '').trim() || 'units';
        byUnit[u] = (byUnit[u] || 0) + Number(m.quantity || 0);
      });
      return Object.entries(byUnit).filter(([, q]) => q > 0).map(([u, q]) => `${Math.round(q * 1000) / 1000} ${u}`).join(' + ') || '0';
    },
    lineSig(mat) {
      return `${mat.product_id || (mat.product && mat.product.id)}|${mat.quantity}|${mat.unit}`;
    },
    async fetchValues() {
      const mats = this.form.stages.flatMap(st => st.materials);
      if (!mats.length) { this.serverValues = {}; return; }
      try {
        const { data } = await axios.post('/api/manufacturing/boms/value-lines', {
          lines: mats.map(m => ({ product_id: m.product_id || (m.product && m.product.id), quantity: Number(m.quantity) || 0, unit: m.unit })),
        });
        const out = {};
        mats.forEach((m, i) => { out[m.temp_uid] = { ...data[i], sig: this.lineSig(m) }; });
        this.serverValues = out;
      } catch (e) { /* keep the on-screen estimate */ }
    },
    lineValue(mat) {
      const r = this.serverValues[mat.temp_uid];
      if (r && r.sig === this.lineSig(mat)) return r.value;
      return this.localLineValue(mat);
    },
    localLineValue(mat) {
      const cost = this.costOf(mat);
      const productUnit = (mat.product && mat.product.measuring_unit) || mat.unit;
      const factor = this.unitFactor(mat.unit, productUnit);
      if (cost === null || factor === null || !(Number(mat.quantity) > 0)) return cost === null || factor === null ? null : 0;
      return Number(mat.quantity) * factor * cost;
    },
    valueHint(mat) {
      const r = this.serverValues[mat.temp_uid];
      if (r && r.sig === this.lineSig(mat) && r.reason) return r.reason;
      return this.costOf(mat) === null
        ? 'This item has no buying price yet. Set it in inventory (or record a purchase) to cost the recipe.'
        : `Can't convert ${mat.unit} to ${mat.product && mat.product.measuring_unit}.`;
    },
    stageValue(stage) {
      return stage.materials.reduce((t, m) => t + (this.lineValue(m) || 0), 0);
    },
    money(n) {
      return Number(n || 0).toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    // ── New ingredient without leaving the form ──
    emptyNewMaterial() {
      return { stage: null, product: '', productcat_id: null, measuring_unit: 'kg', wholesale_ppu: null, can_be_sold: false, saving: false, error: null };
    },
    canCreateMaterial(stage) {
      const q = (stage.materialSearch || '').trim().toLowerCase();
      return q.length >= 2 && !this.allInventoryItems.some(i => (i.product || '').trim().toLowerCase() === q);
    },
    async startNewMaterial(si) {
      const stage = this.form.stages[si];
      this.newMaterial = { ...this.emptyNewMaterial(), stage: si, product: (stage.materialSearch || '').trim() };
      stage.showDropdown = false;
      if (!this.categories.length) {
        try { this.categories = (await axios.get('/api/manufacturing/boms/material-categories')).data; } catch (e) { this.categories = []; }
      }
      if (this.categories.length === 1) this.newMaterial.productcat_id = this.categories[0].id;
    },
    async saveNewMaterial(si) {
      const nm = this.newMaterial;
      nm.saving = true;
      nm.error = null;
      try {
        const { data } = await axios.post('/api/manufacturing/boms/materials', {
          product: nm.product, productcat_id: nm.productcat_id, measuring_unit: nm.measuring_unit,
          wholesale_ppu: nm.wholesale_ppu === '' ? null : nm.wholesale_ppu, can_be_sold: nm.can_be_sold,
        });
        this.allInventoryItems.push(data);
        this.addMaterial(si, data);
        this.newMaterial = this.emptyNewMaterial();
        this.$toasts.push({ type: 'success', message: `${data.product} added to inventory.` });
      } catch (e) {
        const d = e.response && e.response.data;
        nm.error = d && d.errors ? Object.values(d.errors).flat().join(' ') : ((d && d.message) || 'Could not create the item.');
      } finally {
        nm.saving = false;
      }
    },

    emptyForm() {
      return {
        grouped_inventory_id: null, output_product_id: null, product_name: '', name: '',
        bom_type: 'fixed', batch_size: null, batch_unit: 'pcs',
        expected_yield_pct: 100, notes: '', conversion_cost_per_unit: 0,
        effective_from: null, external_reference: null,
        stages: [this.emptyStage(1)],
      };
    },
    emptyStage(order) {
      return {
        temp_uid: uid(), name: '', stage_order: order,
        description: '', expected_wastage_pct: 0, wastage_tracking: 'optional',
        materials: [], materialSearch: '', materialSuggestions: [], showDropdown: false,
      };
    },
    addStage() { this.form.stages.push(this.emptyStage(this.form.stages.length + 1)); },
    removeStage(si) {
      this.form.stages.splice(si, 1);
      this.form.stages.forEach((s, i) => { s.stage_order = i + 1; });
    },
    addMaterial(si, item) {
      const stage = this.form.stages[si];
      // One line per ingredient across all stages (the server refuses duplicates too).
      const used = this.form.stages.find(st => st.materials.some(m => (m.product_id || (m.product && m.product.id)) === item.id));
      if (used) {
        this.$toasts.push({ type: 'danger', message: `${item.product} is already in this recipe${used !== stage ? ` (stage "${used.name || used.stage_order}")` : ''}. Change its quantity instead.` });
        stage.materialSearch = ''; stage.showDropdown = false; stage.materialSuggestions = [];
        return;
      }
      stage.materials.push({
        temp_uid: uid(), product: Object.assign({}, item), product_id: item.id,
        quantity: 1, unit: item.measuring_unit || 'pcs',
        is_required: true, is_substitutable: false, wastage_allowance_pct: 0,
      });
      stage.materialSearch = ''; stage.showDropdown = false; stage.materialSuggestions = [];
    },
    removeMaterial(si, mi) { this.form.stages[si].materials.splice(mi, 1); },
    onMaterialSearch(si) {
      const stage = this.form.stages[si];
      // Typing again after picking one (focus never left the box) must reopen the list.
      stage.showDropdown = true;

      const q = (stage.materialSearch || '').toLowerCase();
       
      if (!q) { stage.materialSuggestions = []; return; }
      // Products with variations can't be recipe materials (the server refuses them too).
      stage.materialSuggestions = this.allInventoryItems
        .filter(i => !i.has_variants && (i.product || '').toLowerCase().includes(q))
        .slice(0, 8);
    },
    async searchProducts() {
      const q = (this.productSearch || '').toLowerCase();
      if (q.length < 2) { this.productSuggestions = []; return; }
      try {
        const { data } = await axios.get('/api/manufacturing/boms/output-products', { params: { search: q } });
        this.productSuggestions = data;
      } catch (e) {}
    },
    selectProduct(p) {
      // The recipe hangs off the product's manufactured group; if it has none yet,
      // the server creates it on save (BomService::groupForProduct).
      this.selectedOutput = p;
      this.form.output_product_id = p.id;
      this.form.grouped_inventory_id = p.grouped_inventory_id || null;
      this.form.product_name = p.product;
      this.productSearch = ''; this.productSuggestions = [];
    },
    async loadInventoryItems() {
      try {
        const { data } = await axios.get('/api/only-products');
        this.allInventoryItems = data.data || data;
      } catch (e) {}
    },
    populateFromBom(bom) {
      this.form.grouped_inventory_id = bom.grouped_inventory_id;
      this.form.output_product_id = bom.grouped_inventory?.output_product_id || null;
      this.form.product_name = bom.grouped_inventory?.output_product?.product || bom.grouped_inventory?.group_title || '';
      this.form.name = bom.name; this.form.bom_type = bom.bom_type;
      this.form.batch_size = bom.batch_size; this.form.batch_unit = bom.batch_unit;
      this.form.expected_yield_pct = bom.expected_yield_pct; this.form.notes = bom.notes || '';
      // Not all of these have inputs here, but an edit must carry them over, not drop them.
      this.form.conversion_cost_per_unit = Number(bom.conversion_cost_per_unit || 0);
      this.form.effective_from = bom.effective_from || null;
      this.form.external_reference = bom.external_reference || null;
      this.form.stages = (bom.stages || []).map(s => ({
        temp_uid: uid(), name: s.name, stage_order: s.stage_order, description: s.description || '',
        expected_wastage_pct: s.expected_wastage_pct, wastage_tracking: s.wastage_tracking,
        materialSearch: '', materialSuggestions: [], showDropdown: false,
        materials: (s.materials || []).map(m => ({
          temp_uid: uid(), product: m.product, product_id: m.product_id,
          quantity: m.quantity, unit: m.unit, is_required: m.is_required,
          is_substitutable: m.is_substitutable, wastage_allowance_pct: m.wastage_allowance_pct,
          notes: m.notes || null,
          substitutes: (m.substitutes || []).map(x => ({
            product_id: x.product_id, priority: x.priority, conversion_factor: x.conversion_factor, notes: x.notes || null,
          })),
        })),
      }));
    },
    reset() { this.form = this.emptyForm(); this.protect = { on: false, pin: '', pin_confirmation: '' }; this.productSearch = ''; this.productSuggestions = []; this.selectedOutput = null; },
    validate() {
      if (!this.form.grouped_inventory_id && !this.form.output_product_id) { this.$toasts.push({ type: 'danger', message: 'Choose the product this recipe makes.' }); return false; }
      if (!this.form.name) { this.$toasts.push({ type: 'danger', message: 'Enter a BOM name.' }); return false; }
      if (!this.form.batch_size) { this.$toasts.push({ type: 'danger', message: 'Enter a batch size.' }); return false; }
      for (const [i, s] of this.form.stages.entries()) {
        if (!s.name) { this.$toasts.push({ type: 'danger', message: `Stage ${i + 1} needs a name.` }); return false; }
      }
      // Stages such as proofing, curing or drying may have no materials, but the recipe needs at least one.
      if (!this.form.stages.some(s => s.materials.length)) { this.$toasts.push({ type: 'danger', message: 'Add at least one material to the recipe.' }); return false; }
      return true;
    },
    async save() {
      if (!this.validate()) return;
      this.saving = true;
      const payload = {
        ...this.form,
        stages: this.form.stages.map(s => ({
          name: s.name, stage_order: s.stage_order, description: s.description,
          expected_wastage_pct: s.expected_wastage_pct, wastage_tracking: s.wastage_tracking,
          materials: s.materials.map(m => ({
            product_id: m.product_id || m.product.id, quantity: m.quantity, unit: m.unit,
            is_required: m.is_required, is_substitutable: m.is_substitutable,
            wastage_allowance_pct: m.wastage_allowance_pct,
            notes: m.notes || null,
            substitutes: m.is_substitutable ? (m.substitutes || []) : [],
          })),
        })),
      };
      try {
        const isEdit = !!this.editBom;
        if (!isEdit && this.protect.on) {
          payload.protect_pin = this.protect.pin;
          payload.protect_pin_confirmation = this.protect.pin_confirmation;
        }
        const method = isEdit ? 'put' : 'post';
        const url    = isEdit ? `/api/manufacturing/boms/${this.editBom.id}` : '/api/manufacturing/boms';
        if (isEdit && this.inPlace) payload.in_place = true;
        const { data } = await axios[method](url, payload);
        this.$emit('saved', data);
        this.reset();
      } catch (e) {
        const d = e.response && e.response.data;
        this.$toasts.push({ type: 'danger', message: d && d.errors ? Object.values(d.errors).flat().join(' ') : ((d && d.message) || 'Save failed.') });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.bom-label { font-size: 12px; font-weight: 600; margin-bottom: 4px; color: #495057; display: block; }

.bom-section-title {
  font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em;
  color: #6c757d; margin-bottom: 10px; padding-bottom: 6px; border-bottom: 1px solid #e9ecef;
}

.bom-stage-block {
  border: 1px solid #e9ecef; border-radius: 8px; margin-bottom: 12px;
}
.bom-stage-top {
  display: flex; align-items: center; gap: 8px;
  padding: 12px 16px 4px; background: #f8f9fa; border-bottom: 1px solid #e9ecef;
  border-radius: 8px 8px 0 0;
}
.bom-stage-num {
  width: 24px; height: 24px; border-radius: 50%; background: #0d6efd; color: #fff;
  font-size: 12px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}

.bom-materials { padding: 12px 16px; }

.bom-mat-table { font-size: 13px; }
.bom-mat-table thead th { font-size: 11px; font-weight: 600; color: #6c757d; text-transform: uppercase; letter-spacing: .04em; }
.bom-mat-name { font-weight: 500; }

.bom-typeahead {
  position: absolute; left: 0; right: 0; z-index: 2000;
  background: #fff; border: 1px solid #dee2e6; border-radius: 8px;
  max-height: 200px; overflow-y: auto; box-shadow: 0 4px 12px rgba(0,0,0,.1); margin-top: 2px;
}
.bom-typeahead-item {
  display: flex; align-items: center; width: 100%; text-align: left;
  padding: 7px 12px; border: none; background: none; cursor: pointer; font-size: 13px;
}
.bom-typeahead-item:hover { background: #f0f4ff; }
.btn-xs { padding: 2px 6px; font-size: 11px; border-radius: 5px; }
</style>
